import React, { useEffect, useState } from 'react';
import { useI18n } from '../hooks/useI18n';
import { getUrlParam } from '../utils/url';
import { getVisitSummary } from '../api/visitSummaryApi';
import { downloadDietPdf } from '../api/dietApi';
import { VisitSummaryResponse, SummaryReferral } from '../types/visit-summary.types';
import { FullscreenLoader } from '../components/ui/FullscreenLoader';
import { RecommendationModal } from '../components/documents/RecommendationModal';
import { PrescriptionModal } from '../components/documents/PrescriptionModal';
import { ReferralModal } from '../components/documents/ReferralModal';
import { DocumentItem as IDocumentItem } from '../types/cabinet.types';

import FileTextIcon from '@/assets/image/file-text.svg?react';
import EyeIcon from '@/assets/image/eye.svg?react';

interface VisitSummaryPageProps {
  onBack: () => void;
}

export const VisitSummaryPage: React.FC<VisitSummaryPageProps> = ({ onBack }) => {
  const { t } = useI18n();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<VisitSummaryResponse | null>(null);
  const [recsModalOpen, setRecsModalOpen] = useState(false);
  const [downloadingIds, setDownloadingIds] = useState<Set<string>>(new Set());
  const [selectedPrescription, setSelectedPrescription] = useState<IDocumentItem | null>(null);
  const [selectedReferral, setSelectedReferral] = useState<SummaryReferral | null>(null);
  const visitId = getUrlParam('visit_id');

  useEffect(() => {
    if (!visitId) {
      setLoading(false);
      return;
    }
    getVisitSummary(visitId)
      .then((res) => {
        setData(res);
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [visitId]);

  const handleDownloadPWA = async (url: string, filename: string, id: string) => {
    try {
      setDownloadingIds(prev => new Set(prev).add(id));
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = window.document.createElement('a');
      link.href = blobUrl;
      const finalFilename = filename.toLowerCase().endsWith('.pdf') ? filename : `${filename}.pdf`;
      link.download = finalFilename;
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Error downloading file in PWA', error);
      window.open(url, '_blank');
    } finally {
      setDownloadingIds(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };



  if (loading) {
    return <FullscreenLoader inline />;
  }

  if (!data) {
    return (
      <div className="cab-visits-page">
        <button className="cab-back-btn" onClick={onBack}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6"></polyline>
          </svg>
          {t('go_back')}
        </button>
        <h1 className="cab-page-title">{t('visit_summary')}</h1>
        <p>{t('no_visit_data') || 'Brak danych o wizycie.'}</p>
      </div>
    );
  }

  const diagnosisText = data.diagnosis?.Details || 
    (data.diagnosis?.DiseaseClassifications && Array.isArray(data.diagnosis.DiseaseClassifications) && data.diagnosis.DiseaseClassifications.length > 0 
      ? data.diagnosis.DiseaseClassifications.map((d: any) => d.FullName || d.Code).join(', ') 
      : null);

  const hasDiagnosis = !!diagnosisText;
  const hasRecommendations = !!data.recommendations?.Recommendations;
  const validPrescriptions = Array.isArray(data.prescriptions)
    ? data.prescriptions.filter(p => !!p.EReceptaCode)
    : [];
  const hasPrescriptions = validPrescriptions.length > 0;
  const hasReferrals = Array.isArray(data.referrals) && data.referrals.length > 0;
  const hasAttachments = Array.isArray(data.attachments) && data.attachments.length > 0;
  const isDietOrder = typeof visitId === 'string' && visitId.toString().startsWith('order_');

  return (
    <div className="cab-visits-page">
      <button className="cab-back-btn" onClick={onBack}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="15 18 9 12 15 6"></polyline>
        </svg>
        {t('go_back')}
      </button>

      <div className="cab-cons-list">
        <h1 className="cab-page-title">{t('visit_summary')}</h1>

        {hasDiagnosis && (
          <div className="cab-summary-section">
            <h3 className="cab-summary-title">{t('diagnosis') || 'Diagnoza'}</h3>
            <div className="cab-summary-card">
              <span className="cab-summary-text-block">{diagnosisText}</span>
            </div>
          </div>
        )}

        {hasRecommendations && (
          <div className="cab-summary-section">
            <h3 className="cab-summary-title">
              {isDietOrder
                ? (t('dietitian_recommendations') || 'Zalecenia dietetyka')
                : (t('doctor_recommendations') || 'Zalecenia lekarza')}
            </h3>
            <div className="cab-summary-card cab-summary-card-interactive" onClick={() => setRecsModalOpen(true)}>
              <div className="cab-summary-card-left">
                <div className="cab-summary-icon">
                  <FileTextIcon />
                </div>
                <div className="cab-summary-card-info">
                  <span className="cab-summary-card-title">
                    {isDietOrder
                      ? (t('dietitian_recommendations') || 'Zalecenia dietetyka')
                      : (t('doctor_recommendations') || 'Zalecenia lekarza')}
                  </span>
                </div>
              </div>
              <div className="cab-summary-card-right">
                <EyeIcon />
              </div>
            </div>
          </div>
        )}

        {hasPrescriptions && (
          <div className="cab-summary-section">
            <h3 className="cab-summary-title">{t('prescription_title') || 'Recepta'}</h3>
            {validPrescriptions.map((prescription) => (
              <div 
                key={prescription.id} 
                className="cab-summary-card cab-summary-card-interactive"
                onClick={() => {
                  setSelectedPrescription({
                    id: prescription.id ? prescription.id.toString() : String(Math.random()),
                    wpId: null,
                    url: '',
                    source: 'medyc',
                    title: t('e_prescription') || 'E-recepta',
                    date: prescription.IssuedDate?.substring(0, 10) || '',
                    type: 'recepta',
                    ereceptaCode: prescription.EReceptaCode,
                    drugs: prescription.Drugs?.map(d => ({
                      name: d.FullName,
                      refundType: d.RefundType,
                      usage: d.Usage,
                      boxAmount: d.BoxAmount
                    })) || []
                  } as IDocumentItem);
                }}
              >
                <div className="cab-summary-card-left">
                  <div className="cab-summary-icon">
                    <FileTextIcon />
                  </div>
                  <div className="cab-summary-card-info">
                    <div className="cab-summary-card-title-row">
                      <span className="cab-summary-card-title">{t('e_prescription') || 'E-recepta'}</span>
                      <span className="cab-pin-badge cab-pin-badge-desktop">{t('e_prescription_code') || 'Kod e-recepty:'} {prescription.EReceptaCode || '---'}</span>
                    </div>
                    <span className="cab-summary-card-subtitle">{prescription.IssuedDate?.substring(0, 10)}</span>
                    <span className="cab-pin-badge cab-pin-badge-mobile">{t('e_prescription_code') || 'Kod e-recepty:'} {prescription.EReceptaCode || '---'}</span>
                  </div>
                </div>
                <div className="cab-summary-card-right">
                  <EyeIcon />
                </div>
              </div>
            ))}
          </div>
        )}

        {hasReferrals && (
          <div className="cab-summary-section">
            <h3 className="cab-summary-title">{t('referrals_title') || 'Skierowania'}</h3>
            {data.referrals!.map((referral) => (
              <div
                key={referral.Id}
                className="cab-summary-card cab-summary-card-interactive"
                onClick={() => setSelectedReferral(referral)}
              >
                <div className="cab-summary-card-left">
                  <div className="cab-summary-icon">
                    <FileTextIcon />
                  </div>
                  <div className="cab-summary-card-info">
                    <div className="cab-summary-card-title-row">
                      <span className="cab-summary-card-title">{t('referral') || 'Skierowanie'}</span>
                      <span className="cab-pin-badge cab-pin-badge-desktop">{t('code_label') || 'Kod:'} {referral.ESkierowanieCode || '---'}</span>
                    </div>
                    <span className="cab-summary-card-subtitle">{referral.CreatedAt?.substring(0, 10)}</span>
                    <span className="cab-pin-badge cab-pin-badge-mobile">{t('code_label') || 'Kod:'} {referral.ESkierowanieCode || '---'}</span>
                  </div>
                </div>
                <div className="cab-summary-card-right">
                  <EyeIcon />
                </div>
              </div>
            ))}
          </div>
        )}

        {hasAttachments && (
          <div className="cab-summary-section">
            <h3 className="cab-summary-title">{t('test_results_docs') || 'Wyniki badań / Dokumenty'}</h3>
            {data.attachments!.map((attachment) => (
              <a 
                key={attachment.Id} 
                href={
                  attachment.Url?.includes('download-pdf')
                    ? attachment.Url + (attachment.Url.includes('?') ? '&' : '?') + 'token=' + encodeURIComponent(sessionStorage.getItem('cab_token') || '')
                    : attachment.Url
                }
                target="_blank" 
                rel="noopener noreferrer" 
                className="cab-summary-card cab-summary-card-interactive"
                onClick={(e) => {
                  if (!attachment.Url) return;
                  const isDietPdf = attachment.Url.includes('download-pdf');
                  // Diet PDF: always fetch via token
                  if (isDietPdf) {
                    e.preventDefault();
                    if (!downloadingIds.has(attachment.Id)) {
                      setDownloadingIds(prev => new Set(prev).add(attachment.Id));
                      downloadDietPdf(attachment.Url, attachment.Name || 'Plan żywieniowy.pdf', true)
                        .finally(() => {
                          setDownloadingIds(prev => {
                            const next = new Set(prev);
                            next.delete(attachment.Id);
                            return next;
                          });
                        });
                    }
                    return;
                  }
                  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
                  const isStandalone = window.matchMedia('(display-mode: standalone)').matches || ('standalone' in navigator && (navigator as any).standalone === true);
                  if (isIOS && isStandalone) {
                    e.preventDefault();
                    if (!downloadingIds.has(attachment.Id)) {
                      handleDownloadPWA(attachment.Url, attachment.Name || 'document', attachment.Id);
                    }
                  }
                }}
              >
                <div className="cab-summary-card-left">
                  <div className="cab-summary-icon">
                    {attachment.Url?.toLowerCase().endsWith('.pdf') ? (
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M14.5 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V7.5L14.5 2Z" stroke="#361777" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M14 2V8H20" stroke="#361777" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M16 13H8" stroke="#361777" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M16 17H8" stroke="#361777" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M10 9H8" stroke="#361777" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    ) : attachment.Url?.match(/\.(jpeg|jpg|gif|png)$/i) != null ? (
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="3" width="18" height="18" rx="2"/>
                        <circle cx="8.5" cy="8.5" r="1.5"/>
                        <polyline points="21 15 16 10 5 21"/>
                      </svg>
                    ) : (
                      <FileTextIcon />
                    )}
                  </div>
                  <div className="cab-summary-card-info">
                    <span className="cab-summary-card-title">{attachment.Name}</span>
                    <span className="cab-summary-card-subtitle">{attachment.UploadedAt?.substring(0, 10)}</span>
                  </div>
                </div>
                <div className="cab-summary-card-right" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  {downloadingIds.has(attachment.Id) ? (
                    <div className="cab-summary-action-ic">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="cab-doc-spin">
                        <polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-.08-6.5"/>
                      </svg>
                    </div>
                  ) : (
                    <>
                      <div className="cab-summary-action-ic" title="Відкрити" onClick={(e) => {
                        // Clicking this will just follow the <a> link normally, but we can ensure it opens
                      }}>
                        <EyeIcon />
                      </div>
                      <div className="cab-summary-action-ic" title="Завантажити" onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (!attachment.Url) return;

                        const isDietPdf = attachment.Url.includes('download-pdf');
                        if (isDietPdf) {
                          if (!downloadingIds.has(attachment.Id)) {
                            setDownloadingIds(prev => new Set(prev).add(attachment.Id));
                            downloadDietPdf(attachment.Url, attachment.Name || 'Plan żywieniowy.pdf', false)
                              .finally(() => {
                                setDownloadingIds(prev => {
                                  const next = new Set(prev);
                                  next.delete(attachment.Id);
                                  return next;
                                });
                              });
                          }
                          return;
                        }

                        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
                        const isStandalone = window.matchMedia('(display-mode: standalone)').matches || ('standalone' in navigator && (navigator as any).standalone === true);
                        
                        if (isIOS && isStandalone) {
                          handleDownloadPWA(attachment.Url, attachment.Name || 'document', attachment.Id);
                        } else {
                          const link = window.document.createElement('a');
                          link.href = attachment.Url;
                          link.download = attachment.Name || 'document';
                          window.document.body.appendChild(link);
                          link.click();
                          window.document.body.removeChild(link);
                        }
                      }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                          <polyline points="7 10 12 15 17 10"/>
                          <line x1="12" y1="15" x2="12" y2="3"/>
                        </svg>
                      </div>
                    </>
                  )}
                </div>
              </a>
            ))}
          </div>
        )}

      </div>

      <RecommendationModal
        visitId={visitId ?? 0}
        isOpen={recsModalOpen}
        onClose={() => setRecsModalOpen(false)}
      />

      <PrescriptionModal
        isOpen={!!selectedPrescription}
        onClose={() => setSelectedPrescription(null)}
        document={selectedPrescription}
      />

      <ReferralModal
        isOpen={!!selectedReferral}
        onClose={() => setSelectedReferral(null)}
        referral={selectedReferral}
      />

      <style>{`
        .cab-visits-page {
          text-align: left;
        }
        .cab-back-btn {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          background: none;
          border: none;
          color: #786798;
          font-family: 'Poppins', sans-serif;
          font-weight: 500;
          font-size: 14px;
          cursor: pointer;
          padding: 0;
          margin-bottom: 24px;
          transition: color 0.2s;
          white-space: nowrap;
        }
        .cab-back-btn:hover {
          color: #361777;
        }
        .cab-page-title {
          font-family: 'Poppins', sans-serif;
          font-weight: 700;
          font-size: 24px;
          color: var(--cab-violet);
          margin: 0 0 24px!important;
        }
        .cab-summary-section {
          margin-bottom: 32px;
        }
        .cab-summary-title {
          font-family: 'Poppins', sans-serif;
          font-weight: 700;
          font-size: 16px;
          color: #361777;
          margin: 0 0 12px!important;
        }
        .cab-summary-card {
          background: #FFFFFF;
          border-radius: 20px;
          padding: 16px 20px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 12px;
          text-decoration: none;
          color: inherit;
        }
        .cab-summary-card-interactive {
          cursor: pointer;
          transition: box-shadow 0.2s, transform 0.2s;
        }
        .cab-summary-card-interactive:hover {
          box-shadow: 0 4px 12px rgba(54, 23, 119, 0.08);
          transform: translateY(-1px);
        }
        .cab-summary-card-left {
          display: flex;
          align-items: center;
          gap: 16px;
        }
        .cab-summary-icon {
          width: 48px;
          height: 48px;
          background: #FAF8FE;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #361777;
        }
        .cab-summary-icon svg {
          width: 24px;
          height: 24px;
        }
        .cab-summary-card-info {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }
        .cab-summary-card-title {
          font-family: 'Poppins', sans-serif;
          font-weight: 700;
          font-size: 15px;
          color: #361777;
        }
        .cab-summary-card-subtitle {
          font-family: 'Poppins', sans-serif;
          font-weight: 500;
          font-size: 13px;
          color: #786798;
        }
        .cab-summary-card-middle {
          flex: 1;
          display: flex;
          justify-content: center;
        }
        .cab-pin-badge {
          background: #E6D6FA;
          color: #361777;
          padding: 6px 16px;
          border-radius: 100px;
          font-family: 'Poppins', sans-serif;
          font-weight: 500;
          font-size: 13px;
        }
        .cab-summary-card-title-row {
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .cab-pin-badge-mobile {
          display: none;
        }
        .cab-summary-card-right {
          color: #361777;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .cab-summary-card-right svg {
          width: 24px;
          height: 24px;
        }
        .cab-summary-action-ic {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 36px;
          height: 36px;
          border-radius: 10px;
          transition: background 0.2s, color 0.2s;
        }
        .cab-summary-action-ic:hover {
          background: #F0EDF6;
          color: #1A5FBF;
        }
        @keyframes cab-spin {
          to { transform: rotate(360deg); }
        }
        .cab-doc-spin {
          animation: cab-spin 1s linear infinite;
        }
        .cab-summary-text-block {
          font-family: 'Poppins', sans-serif;
          font-weight: 600;
          font-size: 15px;
          color: #361777;
          line-height: 1.5;
        }
        @media (max-width: 768px) {
          .cab-pin-badge-desktop {
            display: none;
          }
          .cab-pin-badge-mobile {
            display: inline-block;
            width: fit-content;
            margin-top: 2px;
            padding: 2px 16px;
    font-size: 11px;
          }
          .cab-cons-list {
            background: #FAF8FE;
            padding: 32px 16px;
            border-radius: 24px;
          }
          .cab-summary-card {
            flex-direction: column;
            align-items: flex-start;
            gap: 16px;
            padding: 16px;
          }
          .cab-summary-card-middle {
            justify-content: flex-start;
            width: 100%;
          }
          .cab-summary-card-interactive {
            flex-direction: row;
            align-items: center;
          }
        }
      `}</style>
    </div>
  );
};
